import React from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutlined as DeleteIcon,
} from "@mui/icons-material";

export const DEFAULT_TABLE_COLUMNS = [
  { key: "description", label: "Description", type: "text" },
  { key: "scope", label: "Specification / Scope", type: "text" },
  { key: "quantity", label: "Quantity", type: "number" },
  { key: "unit_cost", label: "Estimated Unit Cost", type: "number" },
  {
    key: "total_cost",
    label: "Estimated Total Cost",
    type: "number",
    calculated: true,
    multiply: ["quantity", "unit_cost"],
  },
];

export const formatCurrency = (val) => {
  const num = Number(val) || 0;
  return `INR ${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const resolveColumns = (cols) => {
  if (!cols) return DEFAULT_TABLE_COLUMNS;
  let parsed = cols;
  if (typeof cols === "string") {
    try {
      parsed = JSON.parse(cols);
    } catch (e) {
      return DEFAULT_TABLE_COLUMNS;
    }
  }
  if (Array.isArray(parsed) && parsed.length > 0) {
    return parsed.map((c, idx) => ({
      key: c.key || `col_${idx + 1}`,
      label: c.label || c.key || `Column ${idx + 1}`,
      type: c.type || "text",
      calculated: Boolean(c.calculated || c.type === "calculated"),
      multiply: Array.isArray(c.multiply) ? c.multiply : null,
    }));
  }
  return DEFAULT_TABLE_COLUMNS;
};

export const createBlankRow = (columns, slNo = 1) => {
  const row = { sl_no: slNo };
  columns.forEach((col) => {
    row[col.key] = col.calculated ? 0 : "";
  });
  return row;
};

export default function DynamicTableField({
  label = "Description of Material / Service Required",
  value = [],
  columns,
  options,
  onChange,
  required = false,
  readOnly = false,
  isDark: propDark,
}) {
  const theme = useTheme();
  const isDark = propDark !== undefined ? propDark : theme.palette.mode === "dark";

  // Resolve custom grid columns configured by the company
  const activeColumns = React.useMemo(() => {
    return resolveColumns(columns || options);
  }, [columns, options]);

  // Compute row calculated values helper
  const computeRowCalculations = (row) => {
    const updated = { ...row };
    activeColumns.forEach((col) => {
      if (col.calculated) {
        if (col.multiply && col.multiply.length === 2) {
          const valA = parseFloat(updated[col.multiply[0]]) || 0;
          const valB = parseFloat(updated[col.multiply[1]]) || 0;
          updated[col.key] = valA * valB;
        } else {
          // Fallback multiplication
          const q = parseFloat(updated.quantity !== undefined ? updated.quantity : updated.qty) || 0;
          const c = parseFloat(updated.unit_cost !== undefined ? updated.unit_cost : updated.rate || updated.price) || 0;
          if (q || c) {
            updated[col.key] = q * c;
          }
        }
      }
    });
    return updated;
  };

  // Ensure value is an array with at least 1 empty row if editable
  const safeRows = React.useMemo(() => {
    if (Array.isArray(value) && value.length > 0) {
      return value.map((r, i) => computeRowCalculations({ ...r, sl_no: i + 1 }));
    }
    return readOnly ? [] : [createBlankRow(activeColumns, 1)];
  }, [value, readOnly, activeColumns]);

  const handleRowChange = (index, fieldKey, val) => {
    if (!onChange || readOnly) return;
    const updated = [...safeRows];
    const currentRow = { ...updated[index], [fieldKey]: val };
    const calculatedRow = computeRowCalculations(currentRow);
    updated[index] = calculatedRow;
    onChange(updated);
  };

  const handleAddRow = () => {
    if (!onChange || readOnly) return;
    const newRow = createBlankRow(activeColumns, safeRows.length + 1);
    onChange([...safeRows, newRow]);
  };

  const handleRemoveRow = (index) => {
    if (!onChange || readOnly) return;
    if (safeRows.length <= 1) return;
    const filtered = safeRows
      .filter((_, i) => i !== index)
      .map((r, i) => ({ ...r, sl_no: i + 1 }));
    onChange(filtered);
  };

  // Grand total calculation for calculated or total columns
  const totalCol = activeColumns.find(
    (c) =>
      c.calculated ||
      c.key.toLowerCase().includes("total") ||
      c.key.toLowerCase().includes("amount") ||
      c.key.toLowerCase().includes("cost")
  );

  const grandTotal = totalCol
    ? safeRows.reduce((acc, row) => acc + (parseFloat(row[totalCol.key]) || 0), 0)
    : 0;

  return (
    <Box sx={{ width: "100%", my: 1.5 }}>
      {/* Header Label */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ color: isDark ? "#c7d2fe" : "#3730a3", fontSize: "0.88rem" }}
        >
          {label} {required && <span style={{ color: "#ef4444" }}>*</span>}
        </Typography>

        {!readOnly && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddRow}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.75rem",
              py: 0.4,
              px: 1.5,
              borderColor: "#6366f1",
              color: "#6366f1",
              "&:hover": { borderColor: "#4f46e5", bgcolor: "rgba(99,102,241,0.06)" },
            }}
          >
            Add Item Row
          </Button>
        )}
      </Stack>

      {/* Table Container */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: "10px",
          border: `1.5px solid ${isDark ? "#334155" : "#cbd5e1"}`,
          bgcolor: isDark ? "rgba(15,23,42,0.6)" : "#ffffff",
          overflowX: "auto",
        }}
      >
        <Table size="small">
          <TableHead sx={{ bgcolor: isDark ? "#1e293b" : "#f1f5f9" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: 60, py: 1, fontSize: "0.78rem" }}>
                Sl. No.
              </TableCell>
              {activeColumns.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    fontWeight: 700,
                    minWidth: col.type === "number" || col.calculated ? 110 : 150,
                    py: 1,
                    fontSize: "0.78rem",
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
              {!readOnly && (
                <TableCell sx={{ width: 48, py: 1, textAlign: "center" }} />
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {safeRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={activeColumns.length + (readOnly ? 1 : 2)} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No items recorded
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              safeRows.map((row, idx) => {
                if (readOnly) {
                  return (
                    <TableRow
                      key={idx}
                      sx={{
                        "&:nth-of-type(even)": {
                          bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.82rem" }}>
                        {idx + 1}
                      </TableCell>
                      {activeColumns.map((col) => {
                        const cellVal = row[col.key];
                        const isCost =
                          col.calculated ||
                          col.key.toLowerCase().includes("cost") ||
                          col.key.toLowerCase().includes("total") ||
                          col.key.toLowerCase().includes("price") ||
                          col.key.toLowerCase().includes("rate");

                        return (
                          <TableCell
                            key={col.key}
                            sx={{
                              fontSize: "0.82rem",
                              fontWeight: col.calculated ? 700 : 500,
                              color: col.calculated ? "#16a34a" : "inherit",
                            }}
                          >
                            {cellVal !== undefined && cellVal !== null && String(cellVal).trim() !== ""
                              ? isCost
                                ? formatCurrency(cellVal)
                                : col.type === "number"
                                ? Number(cellVal).toLocaleString()
                                : String(cellVal)
                              : "—"}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                }

                return (
                  <TableRow
                    key={idx}
                    sx={{
                      "&:nth-of-type(even)": {
                        bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                      },
                    }}
                  >
                    {/* Sl No */}
                    <TableCell sx={{ fontWeight: 800, fontSize: "0.82rem", color: "#4f46e5" }}>
                      {idx + 1}
                    </TableCell>

                    {/* Dynamic Columns */}
                    {activeColumns.map((col) => {
                      if (col.calculated) {
                        return (
                          <TableCell key={col.key} sx={{ py: 0.75 }}>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              sx={{ color: "#16a34a", fontSize: "0.82rem", whiteSpace: "nowrap" }}
                            >
                              {formatCurrency(row[col.key])}
                            </Typography>
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell key={col.key} sx={{ py: 0.75 }}>
                          <TextField
                            size="small"
                            fullWidth
                            type={col.type === "number" ? "number" : col.type === "date" ? "date" : "text"}
                            placeholder={col.label}
                            value={row[col.key] !== undefined ? row[col.key] : ""}
                            onChange={(e) => handleRowChange(idx, col.key, e.target.value)}
                            inputProps={col.type === "number" ? { min: 0, step: "any" } : undefined}
                            InputLabelProps={col.type === "date" ? { shrink: true } : undefined}
                            sx={{
                              "& .MuiOutlinedInput-input": { fontSize: "0.82rem", py: 0.65 },
                              "& .MuiOutlinedInput-root": {
                                bgcolor: isDark ? "#1e293b" : "#fff",
                                borderRadius: "6px",
                              },
                            }}
                          />
                        </TableCell>
                      );
                    })}

                    {/* Delete Row Button */}
                    <TableCell sx={{ py: 0.75, textAlign: "center" }}>
                      <Tooltip title="Remove row">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={safeRows.length <= 1}
                            onClick={() => handleRemoveRow(idx)}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>

          {/* Table Footer with Grand Total */}
          {safeRows.length > 0 && totalCol && (
            <TableFooter sx={{ bgcolor: isDark ? "#1e293b" : "#f1f5f9" }}>
              <TableRow>
                <TableCell
                  colSpan={Math.max(1, activeColumns.length - 1)}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    textAlign: "right",
                    py: 1.2,
                  }}
                >
                  Grand Total ({totalCol.label}):
                </TableCell>
                <TableCell
                  colSpan={readOnly ? 2 : 3}
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.9rem",
                    color: "#16a34a",
                    py: 1.2,
                  }}
                >
                  {formatCurrency(grandTotal)}
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
}
